import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Lock, Loader2, Save, AlertTriangle, Key, Plus, Wrench, Calendar, 
  User, MapPin, Phone, Trash2, FileText, Download, Eye, EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getIdToken } from "../lib/firebase";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "working", label: "Working" },
  { value: "under_maintenance", label: "Under Maintenance" },
  { value: "not_working", label: "Not Working" },
];

const shiftOptions = [
  { value: "shift_1", label: "Shift 1" },
  { value: "shift_2", label: "Shift 2" },
  { value: "shift_3", label: "Shift 3" },
];

const QREditPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [qrData, setQRData] = useState(null);
  const [formData, setFormData] = useState({});
  const [pin, setPin] = useState("");
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("info"); // info, instructions, logs
  const [showPin, setShowPin] = useState(false);
  
  // Helper function to get current date in DD/MM/YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  // Get logged-in user's name for auto-fill
  const getLoggedInUserName = () => {
    return userProfile?.name || user?.displayName || "";
  };
  
  const [newLog, setNewLog] = useState({
    date: getCurrentDate(),
    shift: "",
    breakdown_time: "",
    breakdown_hour: "",
    breakdown_minute: "",
    breakdown_period: "",
    maintenance_duration: "",
    duration_hours: "",
    duration_minutes: "",
    issue: "",
    action: "",
    parts_replaced: "",
    technician: "",
    next_service: ""
  });
  
  // Update technician name when userProfile loads
  useEffect(() => {
    if (userProfile?.name || user?.displayName) {
      setNewLog(prev => ({
        ...prev,
        technician: prev.technician || getLoggedInUserName()
      }));
    }
  }, [userProfile, user]);

  useEffect(() => {
    // Wait for auth to load before fetching
    if (!authLoading) {
      if (user) {
        fetchQRData();
      } else {
        // Not authenticated, redirect to login
        toast.error("Please login to edit QR codes");
        navigate("/login", { state: { from: `/edit/${code}` } });
      }
    }
  }, [code, user, authLoading]);

  const fetchQRData = async () => {
    try {
      // Use Firebase token instead of localStorage
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required. Please login again.");
      }
      
      const response = await fetch(`${API}/qr/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login", { state: { from: `/edit/${code}` } });
          return;
        }
        throw new Error(data.detail || "Failed to fetch QR data");
      }

      setQRData(data);
      setFormData(data.data || {});
    } catch (error) {
      toast.error(error.message);
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPIN = async () => {
    if (!pin) {
      toast.error("Please enter your PIN");
      return;
    }

    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Session expired. Please login again.");
      }
      
      const response = await fetch(`${API}/qr/${code}/verify-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pin }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login", { state: { from: `/edit/${code}` } });
          return;
        }
        throw new Error(data.detail || "Invalid PIN");
      }

      setVerified(true);
      toast.success("PIN verified");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddMaintenanceLog = () => {
    if (!newLog.date || !newLog.issue) {
      toast.error("Please enter at least Date and Issue Observed");
      return;
    }

    const logs = formData.maintenance_logs || [];
    // Add new log at the beginning (newest first)
    const updatedLogs = [{ ...newLog, added_at: new Date().toISOString() }, ...logs];
    
    setFormData(prev => ({ ...prev, maintenance_logs: updatedLogs }));
    setNewLog({
      date: getCurrentDate(),
      shift: "",
      breakdown_time: "",
      breakdown_hour: "",
      breakdown_minute: "",
      breakdown_period: "",
      maintenance_duration: "",
      duration_hours: "",
      duration_minutes: "",
      issue: "",
      action: "",
      parts_replaced: "",
      technician: getLoggedInUserName(),
      next_service: ""
    });
    toast.success("Maintenance log added");
  };

  const handleDeleteLog = (index) => {
    const logs = formData.maintenance_logs || [];
    const updatedLogs = logs.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, maintenance_logs: updatedLogs }));
    toast.success("Log entry removed");
  };

  // Export to Excel
  const handleExportExcel = () => {
    const logs = formData.maintenance_logs || [];
    if (logs.length === 0) {
      toast.error("No maintenance records to export");
      return;
    }

    const machineName = formData.machine_name || "Machine";
    const machineId = formData.machine_id || code;

    // Prepare data for Excel
    const excelData = logs.map((log, index) => ({
      "S.No": index + 1,
      "Date": log.date || "",
      "Shift": log.shift ? log.shift.replace("_", " ").toUpperCase() : "",
      "Breakdown Time": log.breakdown_time || "",
      "Maintenance Duration": log.maintenance_duration || "",
      "Issue Observed": log.issue || "",
      "Action Taken": log.action || "",
      "Parts Replaced": log.parts_replaced || "",
      "Technician": log.technician || "",
      "Next Service Date": log.next_service || ""
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 18 },
      { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Maintenance Records");

    // Generate filename and download
    const fileName = `${machineName.replace(/\s+/g, '_')}_${machineId}_Maintenance_Records.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Excel file downloaded successfully");
  };

  // Export to PDF
  const handleExportPDF = () => {
    const logs = formData.maintenance_logs || [];
    if (logs.length === 0) {
      toast.error("No maintenance records to export");
      return;
    }

    const machineName = formData.machine_name || "Machine";
    const machineId = formData.machine_id || code;

    // Create PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Add title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Maintenance Records", 14, 15);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Machine: ${machineName}`, 14, 23);
    doc.text(`Machine ID: ${machineId}`, 14, 29);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 35);

    // Prepare table data
    const tableData = logs.map((log, index) => [
      index + 1,
      log.date || "-",
      log.shift ? log.shift.replace("_", " ").toUpperCase() : "-",
      log.breakdown_time || "-",
      log.maintenance_duration || "-",
      log.issue || "-",
      log.action || "-",
      log.parts_replaced || "-",
      log.technician || "-",
      log.next_service || "-"
    ]);

    // Add table
    doc.autoTable({
      startY: 42,
      head: [[
        "S.No", "Date", "Shift", "Breakdown\nTime", "Duration",
        "Issue Observed", "Action Taken", "Parts Replaced", "Technician", "Next Service"
      ]],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [37, 99, 235], 
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: { 
        fontSize: 7,
        cellPadding: 2
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 20 },
        2: { cellWidth: 18 },
        3: { cellWidth: 20 },
        4: { cellWidth: 18 },
        5: { cellWidth: 45 },
        6: { cellWidth: 45 },
        7: { cellWidth: 30 },
        8: { cellWidth: 25 },
        9: { cellWidth: 22 }
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap'
      }
    });

    // Save PDF
    const fileName = `${machineName.replace(/\s+/g, '_')}_${machineId}_Maintenance_Records.pdf`;
    doc.save(fileName);
    toast.success("PDF file downloaded successfully");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error("Session expired. Please login again.");
      }
      
      const response = await fetch(`${API}/qr/${code}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: formData, pin }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Session expired. Please login again.");
          navigate("/login", { state: { from: `/edit/${code}` } });
          return;
        }
        throw new Error(data.detail || "Failed to update");
      }

      toast.success("Machine details updated successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Show loading while auth is checking or data is loading
  if (authLoading || (loading && !qrData)) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-6">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <p className="text-sm text-[#6B7280]">Edit Machine</p>
            <p className="font-bold text-[#1F2933] font-mono">{code}</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Locked State Warning */}
        {qrData?.is_locked && (
          <Card className="bg-[#DC2626]/10 border-[#DC2626]/20 mb-4">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-[#DC2626]" />
              <div>
                <p className="font-medium text-[#DC2626]">QR Code Locked</p>
                <p className="text-sm text-[#DC2626]/80">
                  Too many failed PIN attempts. 
                  <button
                    onClick={() => navigate(`/forgot-pin/${code}`)}
                    className="underline ml-1"
                  >
                    Reset PIN
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PIN Verification */}
        {!verified && !qrData?.is_locked && (
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#2563EB]" />
                Enter PIN to Edit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>PIN</Label>
                <div className="relative">
                  <Input
                    type={showPin ? "text" : "password"}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="••••••"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    className="h-12 text-center text-xl tracking-widest pr-10"
                    data-testid="pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="pin-toggle"
                  >
                    {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                onClick={handleVerifyPIN}
                disabled={loading || !pin}
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8]"
                data-testid="verify-pin-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Lock className="w-5 h-5 mr-2" />
                )}
                Verify PIN
              </Button>
              <div className="text-center">
                <button
                  onClick={() => navigate(`/forgot-pin/${code}`)}
                  className="text-sm text-[#2563EB] hover:underline"
                  data-testid="forgot-pin-link"
                >
                  <Key className="w-4 h-4 inline mr-1" />
                  Forgot PIN?
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Form */}
        {verified && (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-4 bg-white p-1 rounded-lg shadow-sm">
              {[
                { id: "info", label: "Machine Info", icon: Wrench },
                { id: "instructions", label: "Instructions", icon: FileText },
                { id: "logs", label: "Maintenance Logs", icon: Calendar },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? "bg-[#2563EB] text-white"
                      : "text-[#6B7280] hover:bg-gray-100"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Machine Info Tab */}
            {activeTab === "info" && (
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-[#2563EB]" />
                    Machine Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    label="Machine Name"
                    placeholder="Enter machine name"
                    value={formData.machine_name}
                    onChange={(v) => handleInputChange("machine_name", v)}
                    required
                  />
                  <FormField
                    label="Machine ID / Number"
                    placeholder="Enter machine ID"
                    value={formData.machine_id}
                    onChange={(v) => handleInputChange("machine_id", v)}
                  />
                  <FormField
                    label="Machine Type"
                    placeholder="Enter machine type"
                    value={formData.machine_type}
                    onChange={(v) => handleInputChange("machine_type", v)}
                  />
                  <FormField
                    label="Location"
                    placeholder="Enter location"
                    value={formData.location}
                    onChange={(v) => handleInputChange("location", v)}
                    icon={MapPin}
                  />
                  <FormField
                    label="Installation Date"
                    placeholder="DD/MM/YYYY"
                    value={formData.installation_date}
                    onChange={(v) => handleInputChange("installation_date", v)}
                  />
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-[#1F2933] mb-3">Responsibility</h4>
                    <div className="space-y-4">
                      <FormField
                        label="Operator Name"
                        placeholder="Enter operator name"
                        value={formData.operator_name}
                        onChange={(v) => handleInputChange("operator_name", v)}
                        icon={User}
                      />
                      <FormField
                        label="Technician Name"
                        placeholder="Enter technician name"
                        value={formData.technician_name}
                        onChange={(v) => handleInputChange("technician_name", v)}
                        icon={User}
                      />
                      <FormField
                        label="Contact Number"
                        placeholder="Enter contact number"
                        value={formData.contact_number}
                        onChange={(v) => handleInputChange("contact_number", v)}
                        icon={Phone}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-[#1F2933] mb-3">Status</h4>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Current Status</Label>
                        <Select 
                          value={formData.current_status || "working"} 
                          onValueChange={(v) => handleInputChange("current_status", v)}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <FormField
                        label="Last Service Date"
                        placeholder="DD/MM/YYYY"
                        value={formData.last_service_date}
                        onChange={(v) => handleInputChange("last_service_date", v)}
                      />
                      <FormField
                        label="Next Service Due"
                        placeholder="DD/MM/YYYY"
                        value={formData.next_service_due}
                        onChange={(v) => handleInputChange("next_service_due", v)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Instructions Tab */}
            {activeTab === "instructions" && (
              <Card className="bg-white shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#2563EB]" />
                    Instructions & Precautions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label className="text-[#16A34A] font-medium">Operating Instructions</Label>
                    <Textarea
                      placeholder="Enter step-by-step operating instructions..."
                      value={formData.operating_instructions || ""}
                      onChange={(e) => handleInputChange("operating_instructions", e.target.value)}
                      className="min-h-[150px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#DC2626] font-medium">Safety Precautions / Do's & Don'ts</Label>
                    <Textarea
                      placeholder="Enter safety precautions and warnings..."
                      value={formData.safety_precautions || ""}
                      onChange={(e) => handleInputChange("safety_precautions", e.target.value)}
                      className="min-h-[150px]"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Maintenance Logs Tab */}
            {activeTab === "logs" && (
              <div className="space-y-4">
                {/* Add New Log */}
                <Card className="bg-white shadow-sm border-2 border-dashed border-[#2563EB]/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-[#2563EB]">
                      <Plus className="w-4 h-4" />
                      Add Maintenance Log
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="Date"
                        placeholder="DD/MM/YYYY"
                        value={newLog.date}
                        onChange={(v) => setNewLog(prev => ({ ...prev, date: v }))}
                        required
                      />
                      <div className="space-y-2">
                        <Label>Shift</Label>
                        <Select 
                          value={newLog.shift} 
                          onValueChange={(v) => setNewLog(prev => ({ ...prev, shift: v }))}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select shift" />
                          </SelectTrigger>
                          <SelectContent>
                            {shiftOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="Technician Name"
                        placeholder="Who performed service"
                        value={newLog.technician}
                        onChange={(v) => setNewLog(prev => ({ ...prev, technician: v }))}
                      />
                      {/* Breakdown Time Picker */}
                      <div className="space-y-2">
                        <Label>Breakdown Time</Label>
                        <div className="flex gap-2">
                          <Select 
                            value={newLog.breakdown_hour || ""} 
                            onValueChange={(v) => setNewLog(prev => ({ 
                              ...prev, 
                              breakdown_hour: v,
                              breakdown_time: `${v}:${prev.breakdown_minute || '00'} ${prev.breakdown_period || 'AM'}`
                            }))}
                          >
                            <SelectTrigger className="h-10 w-20">
                              <SelectValue placeholder="Hr" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...Array(12)].map((_, i) => (
                                <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>
                                  {String(i + 1).padStart(2, '0')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="flex items-center text-gray-500">:</span>
                          <Select 
                            value={newLog.breakdown_minute || ""} 
                            onValueChange={(v) => setNewLog(prev => ({ 
                              ...prev, 
                              breakdown_minute: v,
                              breakdown_time: `${prev.breakdown_hour || '12'}:${v} ${prev.breakdown_period || 'AM'}`
                            }))}
                          >
                            <SelectTrigger className="h-10 w-20">
                              <SelectValue placeholder="Min" />
                            </SelectTrigger>
                            <SelectContent>
                              {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select 
                            value={newLog.breakdown_period || ""} 
                            onValueChange={(v) => setNewLog(prev => ({ 
                              ...prev, 
                              breakdown_period: v,
                              breakdown_time: `${prev.breakdown_hour || '12'}:${prev.breakdown_minute || '00'} ${v}`
                            }))}
                          >
                            <SelectTrigger className="h-10 w-20">
                              <SelectValue placeholder="AM" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AM">AM</SelectItem>
                              <SelectItem value="PM">PM</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Maintenance Duration Picker */}
                      <div className="space-y-2">
                        <Label>Maintenance Duration</Label>
                        <div className="flex gap-2 items-center">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              max="99"
                              placeholder="0"
                              value={newLog.duration_hours || ""}
                              onChange={(e) => {
                                const hrs = e.target.value;
                                setNewLog(prev => ({ 
                                  ...prev, 
                                  duration_hours: hrs,
                                  maintenance_duration: `${hrs || '0'} Hr ${prev.duration_minutes || '0'} Min`
                                }));
                              }}
                              className="h-10 w-16 text-center"
                            />
                            <span className="text-sm font-medium text-gray-600">Hr</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              max="59"
                              placeholder="0"
                              value={newLog.duration_minutes || ""}
                              onChange={(e) => {
                                const mins = e.target.value;
                                setNewLog(prev => ({ 
                                  ...prev, 
                                  duration_minutes: mins,
                                  maintenance_duration: `${prev.duration_hours || '0'} Hr ${mins || '0'} Min`
                                }));
                              }}
                              className="h-10 w-16 text-center"
                            />
                            <span className="text-sm font-medium text-gray-600">Min</span>
                          </div>
                        </div>
                      </div>
                      <FormField
                        label="Next Service Date (optional)"
                        placeholder="DD/MM/YYYY"
                        value={newLog.next_service}
                        onChange={(v) => setNewLog(prev => ({ ...prev, next_service: v }))}
                      />
                    </div>
                    <FormField
                      label="Issue Observed"
                      placeholder="Describe the issue or reason for maintenance"
                      value={newLog.issue}
                      onChange={(v) => setNewLog(prev => ({ ...prev, issue: v }))}
                      multiline
                      required
                    />
                    <FormField
                      label="Action Taken"
                      placeholder="Describe what was done to fix/maintain"
                      value={newLog.action}
                      onChange={(v) => setNewLog(prev => ({ ...prev, action: v }))}
                      multiline
                    />
                    <FormField
                      label="Parts Replaced (optional)"
                      placeholder="List any parts that were replaced"
                      value={newLog.parts_replaced}
                      onChange={(v) => setNewLog(prev => ({ ...prev, parts_replaced: v }))}
                    />
                    <Button
                      onClick={handleAddMaintenanceLog}
                      className="w-full bg-[#2563EB] hover:bg-[#1D4ED8]"
                      data-testid="add-log-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Log Entry
                    </Button>
                  </CardContent>
                </Card>

                {/* Export Buttons */}
                {(formData.maintenance_logs || []).length > 0 && (
                  <Card className="bg-white shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Download className="w-4 h-4 text-[#2563EB]" />
                        Export Maintenance Records
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-3">
                        <Button
                          onClick={handleExportExcel}
                          variant="outline"
                          className="flex-1 border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A]/10"
                          data-testid="export-excel-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export Excel
                        </Button>
                        <Button
                          onClick={handleExportPDF}
                          variant="outline"
                          className="flex-1 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626]/10"
                          data-testid="export-pdf-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export PDF
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Existing Logs */}
                {(formData.maintenance_logs || []).length > 0 && (
                  <Card className="bg-white shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">
                        Existing Logs ({(formData.maintenance_logs || []).length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(formData.maintenance_logs || []).map((log, index) => (
                        <div 
                          key={index} 
                          className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-medium text-[#1F2933]">{log.date}</span>
                              {log.shift && (
                                <span className="text-xs ml-2 px-2 py-0.5 bg-[#2563EB]/10 text-[#2563EB] rounded">
                                  {log.shift.replace("_", " ").toUpperCase()}
                                </span>
                              )}
                              {log.technician && (
                                <span className="text-sm text-[#6B7280] ml-2">by {log.technician}</span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLog(index)}
                              className="text-[#DC2626] hover:bg-[#DC2626]/10 h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {(log.breakdown_time || log.maintenance_duration) && (
                            <div className="flex flex-wrap gap-3 mb-2 text-xs text-[#92400E] bg-[#FEF3C7] p-2 rounded">
                              {log.breakdown_time && (
                                <span><strong>Breakdown:</strong> {log.breakdown_time}</span>
                              )}
                              {log.maintenance_duration && (
                                <span><strong>Duration:</strong> {log.maintenance_duration}</span>
                              )}
                            </div>
                          )}
                          {log.issue && (
                            <p className="text-sm text-[#4B5563] mb-1">
                              <span className="text-[#9CA3AF]">Issue:</span> {log.issue}
                            </p>
                          )}
                          {log.action && (
                            <p className="text-sm text-[#16A34A]">
                              <span className="text-[#9CA3AF]">Action:</span> {log.action}
                            </p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 mt-6 bg-[#16A34A] hover:bg-[#15803D]"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              Save All Changes
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const FormField = ({ label, placeholder, value, onChange, required, multiline, icon: Icon }) => (
  <div className="space-y-2">
    <Label>
      {label}
      {required && <span className="text-[#DC2626] ml-1">*</span>}
    </Label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
      )}
      {multiline ? (
        <Textarea
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[80px]"
        />
      ) : (
        <Input
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`h-12 ${Icon ? 'pl-10' : ''}`}
        />
      )}
    </div>
  </div>
);

export default QREditPage;
